<script setup lang="ts">
import { useResponsive } from '@shared/nuxt/useResponsive'

type Props = {
  modelValue?: string
  isInputFocused: boolean
  detectedCityName?: string
  detectedCityUrl?: string
  isGeoLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  detectedCityName: '',
  detectedCityUrl: '',
  isGeoLoading: true,
})

type Emits = {
  'update:model-value': [value: string]
  changeFocus: [state: boolean]
}

const emit = defineEmits<Emits>()

const { isMobile } = useResponsive()
const searchInputContainer = ref<HTMLElement | null>(null)

const searchValue = computed(() => {
  if (props.isInputFocused || props.modelValue || !props.detectedCityName) {
    return props.modelValue
  }

  return props.detectedCityName
})

const isShowMenuBtn = computed(() => Boolean(props.detectedCityUrl))

const emitSearchQuery = (value: string | undefined) => {
  emit('update:model-value', value ?? '')
}

const changeFocusCityInput = (isFocused: boolean) => {
  emit('changeFocus', isFocused)
}

const onInputFocus = () => changeFocusCityInput(true)

const onClickOtherCity = () => {
  const searchInput = searchInputContainer.value?.querySelector('input')

  if (searchInput instanceof HTMLElement) {
    searchInput.focus()
    changeFocusCityInput(true)
  }
}
</script>

<template>
  <div class="select-city">
    <h2 class="select-city__title">Перейти на сайт города</h2>

    <div
      id="searchInput"
      ref="searchInputContainer"
      class="select-city__search-city"
      @click="changeFocusCityInput(true)"
    >
      <the-input
        :model-value="searchValue"
        class="select-city__search-input"
        placeholder="Введите название города"
        aria-disabled="true"
        hide-details
        name="searchInput"
        :clearable="false"
        @update:model-value="emitSearchQuery"
        @focus="onInputFocus"
      />
    </div>

    <div class="select-city__btns">
      <div v-if="isShowMenuBtn" class="select-city__menu-btn">
        <the-button :href="detectedCityUrl" block target="_blank">
          Перейти к меню

          <the-icon name="arrow-right" size="32" color="currentColor" />
        </the-button>
      </div>

      <div id="btnOtherCity" class="select-city__other-city-btn">
        <the-button
          :variant="isMobile ? 'secondary' : 'tertiary'"
          :loading="isGeoLoading"
          block
          @click="onClickOtherCity"
        >
          Другой город
        </the-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select-city {
  margin-bottom: 20px;

  @include media($bp-tablet) {
    margin-bottom: 80px;
  }

  @include media($bp-desktop) {
    margin-bottom: 0;
  }

  &__title {
    height: 28px;
    margin-bottom: 30px;
    font-size: 24px;
    font-weight: 800;
    line-height: 28px;
    color: var(--ui-color-white);
    transition: 0.3s;

    @include media($bp-tablet) {
      max-width: 478px;
      height: 94px;
      font-size: 40px;
      line-height: 47px;
    }
  }

  &__search-city {
    margin-bottom: 40px;
  }

  &__search-input {
    width: 100%;

    @include media($bp-tablet) {
      max-width: 501px;
    }

    :deep(.app-input__control) {
      padding: 0;
      background-color: transparent;
      border: none;
      border-bottom: 4px solid var(--ui-color-primary);
      border-radius: unset;

      &:hover {
        background-color: transparent;
      }
    }

    :deep(.app-input__field) {
      font-size: 24px;
      font-weight: 400;
      line-height: unset;
      color: var(--ui-color-white);

      @include media($bp-tablet) {
        font-size: 26px;
      }

      &::placeholder {
        font-size: inherit;
        line-height: unset;
        color: var(--ui-color-sandy-200);
      }
    }
  }

  &__btns {
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: max-content;
    max-height: 120px;
    transition: 0.3s;

    @include media($bp-tablet) {
      flex-direction: row;
      gap: 30px;
      height: 50px;
      max-height: unset;
    }
  }

  &__menu-btn {
    width: 100%;

    @include media($bp-tablet) {
      width: 310px;
    }
  }

  &__other-city-btn {
    width: 100%;

    @include media($bp-tablet) {
      width: max-content;
    }
  }
}

.cities-block--active-search {
  .select-city {
    width: 100%;
    margin-bottom: 0;

    @include media($bp-tablet) {
      width: max-content;
    }

    &__title {
      height: 0;
      margin-bottom: 0;
      overflow: hidden;
      opacity: 0;
    }

    &__btns {
      max-height: 0;
      overflow: hidden;
      opacity: 0;
    }

    &__search-city {
      margin-bottom: 20px;

      @include media($bp-tablet) {
        margin-bottom: 40px;
      }
    }
  }
}
</style>
